import React, { useState, useContext, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import UserContext from "../context/UserContext";
import { api } from "../utils/apiHelper";
import "../styles/DigiStore.css";

const DigiStore = () => {
  const { authUser } = useContext(UserContext);
  const { projectId: paramProjectId } = useParams();
  const projectId = paramProjectId || uuidv4();

  const [projectTitle, setProjectTitle] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [accessType, setAccessType] = useState("private");
  const [licenseType, setLicenseType] = useState("free");
  const [uploading, setUploading] = useState(false);

  // Dropzone for images
  const onImageDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(URL.createObjectURL(file));
    setImageFile(file);
  }, []);
  const { getRootProps: getImageRootProps, getInputProps: getImageInputProps } = useDropzone({
    accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/gif": [".gif"] },
    onDrop: onImageDrop,
    multiple: false,
  });

 const getProductFileType = (file) => {
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type.startsWith("video/")) return "video";
  return "document";
};

const onProductFileDrop = useCallback((acceptedFiles) => {
  setProductFiles((prevFiles) => [
    ...prevFiles,
    ...acceptedFiles.map(file => ({
      id: uuidv4(),
      title: file.name.replace(/\.[^/.]+$/, ""),
      file,
      fileType: getProductFileType(file),
      fileId: null,
      url: null,
    }))
  ]);
}, []);

const { getRootProps: getProductRootProps, getInputProps: getProductInputProps } = useDropzone({
  accept: {
    "audio/*": [".mp3", ".m4a", ".wav", ".aac"],
    "video/*": [".mp4", ".mov", ".webm"],
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/epub+zip": [".epub"],
    "text/plain": [".txt"]
  },
  onDrop: onProductFileDrop,
  multiple: true,
});

const handleProductTitleChange = (index, title) => {
  setProductFiles((prevFiles) => {
    const updatedFiles = [...prevFiles];
    updatedFiles[index] = {
      ...updatedFiles[index],
      title,
    };
    return updatedFiles;
  });
};
const removeProductFile = (id) => {
  setProductFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
};


  const extractMetadata = async (fileKey) => {
    try {
      const response = await api("/extract-audio-metadata", "POST", { fileKey });
      return response.metadata;
    } catch (error) {
      console.error("Error extracting metadata:", error);
      return null;
    }
  };

  

  const uploadToS3 = async (file, folder) => {
    try {
      const userId = authUser?._id;
      if (!userId) throw new Error("User not authenticated");

      const response = await api("/digi-signed-url", "POST", {
        fileName: file.name,
        fileType: file.type,
        accessType,
        userId,
        projectId,
        folder
      });

      if (!response.url || !response.key) return null;

      await fetch(response.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      return response.key;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUploading(true);
    const userId = authUser?._id;

    if (!userId) {
      console.error("User not authenticated. Cannot submit.");
      setUploading(false);
      return;
    }

    let imageKey = null;
    if (imageFile) {
      const folder = `users/${userId}/digiStore/${projectId}/images`;
      imageKey = await uploadToS3(imageFile, folder);
    }

 const uploadedProductFiles = await Promise.all(
  productFiles.map(async (file) => {
    if (!file.file) return file;

    const folder = `users/${userId}/digiStore/${projectId}/${file.fileType}Files`;
    const fileKey = await uploadToS3(file.file, folder);

    let metadata = null;

    if (file.fileType === "audio") {
      metadata = await extractMetadata(fileKey);
    }

    return {
      ...file,
      fileId: fileKey,
      metadata,
      url: `https://editfarm.s3.us-east-2.amazonaws.com/${fileKey}`,
    };
  })
);

const validProductFiles = uploadedProductFiles.filter((file) => file.fileId);

   

      const fileKeys = validProductFiles.map((file) => ({
        key: file.fileId,
        fileName: file.file.name,
        title: file.title,
        fileType: file.fileType,
        metadata: file.metadata,
        url: file.url,
      }));

    const payload = {
        userId,
        projectId,
        projectTitle,
        description,
        price,
        accessType,
        licenseType,
        image: imageKey,
        fileKeys,
        quantity: quantity !== "" ? Number(quantity) : undefined
      };

    try {
      const response = await api("/digi-upload-product", "POST", payload);

      if (response.error) {
        console.error("Submission failed:", response.error);
        setUploading(false);
        return;
      }

      setProductFiles([]);
      setImage(null);
      setImageFile(null);
      setProjectTitle("");
      setDescription("");
      setPrice("");
      setAccessType("private");
      setLicenseType("free");
      setQuantity("");
      window.alert("Upload successful!");
      console.log("Submission successful.");
    } catch (error) {
     window.alert("An unexpected error occurred. Please try again.");
      console.error("Error submitting product:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
   <div className="digi-store-wrapper">
  <h2 className="digi-store-title">Create Your Product</h2>

  <form onSubmit={handleSubmit}>
    <div className="digi-form-group">
      <label className="digi-form-label">Product Title</label>
      <input
        type="text"
        className="digi-input"
        value={projectTitle}
        onChange={(e) => setProjectTitle(e.target.value)}
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">Price ($)</label>
      <input
        type="number"
        className="digi-input"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">Description</label>
      <textarea
        className="digi-input"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">Quantity (optional)</label>
      <input
        type="number"
        min="0"
        max="1000"
        className="digi-input"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        placeholder="optional"
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">License Type</label>
      <select
        className="digi-input"
        value={licenseType}
        onChange={(e) => setLicenseType(e.target.value)}
      >
        <option value="free">Free License (Non-Commercial Use)</option>
        <option value="non-exclusive">Non-Exclusive License</option>
        <option value="exclusive">Exclusive License</option>
      </select>
    </div>

    <h3>Upload Cover Image</h3>
    <div {...getImageRootProps()} className="digi-dropzone">
      <input {...getImageInputProps()} />
      <p>Drag & drop an image or tap to select</p>
    </div>
    {image && <img src={image} alt="Preview" className="digi-preview-image" />}

   <h3>Product Files</h3>

<div {...getProductRootProps()} className="digi-dropzone">
  <input {...getProductInputProps()} />
  <p>
    Drag & drop audio, video, PDF, DOCX, EPUB, TXT files
    or tap to select
  </p>
</div>

<div className="digi-audio-list">
  {productFiles.map((file, index) => (
    <div key={file.id} className="digi-audio-item">
      <input
        type="text"
        className="digi-input"
        value={file.title}
        onChange={(e) =>
          handleProductTitleChange(index, e.target.value)
        }
      />

      <span>{file.file?.name}</span>

      <span
        style={{
          fontSize: "12px",
          color: "#666",
          marginLeft: "10px"
        }}
      >
        {file.fileType}
      </span>

      <button
        type="button"
        className="digi-remove-button"
        onClick={() => removeProductFile(file.id)}
      >
        Remove
      </button>
    </div>
  ))}
</div>

    <button type="submit" className="digi-button" disabled={uploading}>
      {uploading ? "Uploading..." : "Submit Product"}
    </button>
  </form>
</div>
  );
};

export default DigiStore;
